<script setup>
import {onBeforeRouteLeave, useRoute, useRouter} from 'vue-router'
import {ref, computed, onBeforeMount, watch} from 'vue'
import {useStore} from 'vuex'

import {DRAFT_STORAGE_KEY, progressStatus, submitStatus} from '@/helpers/utils';
import {frontendConfig} from '@/helpers/frontend_config';
import {TOOLTIPS} from '@/helpers/constants';

import Alert from '@/components/Alert.vue';
import ProgressBar from '@/components/ProgressBar.vue';
import Modal from '@/components/Modal.vue';
import LocationForm from '@/components/add/LocationForm.vue';
import SearchLocationForm from '@/components/add/SearchLocationForm.vue';
import SelectDetails from '@/components/add/SelectDetails.vue';
import ResidentForm from '@/components/add/ResidentForm.vue';
import GADForm from '@/components/add/GADForm.vue';
import CensusForm from '@/components/add/CensusForm.vue';
import StructureForm from '@/components/add/StructureForm.vue';
import EventForm from '@/components/add/EventForm.vue';
import PhotoForm from '@/components/add/PhotoForm.vue';
import ReferenceForm from '@/components/add/ReferenceForm.vue';
import ReviewDetailWrapper from '@/views/ReviewDetailWrapper.vue';
import SubmitSection from '@/components/add/SubmitSection.vue';
import {useAuth0} from "@auth0/auth0-vue";
import _ from "lodash";
import AddHomeLoginPrompt from "@/components/add/AddHomeLoginPrompt.vue";

const formComponents = {
  LocationForm,
  SearchLocationForm,
  SelectDetails,
  ResidentForm,
  GADForm,
  CensusForm,
  StructureForm,
  EventForm,
  PhotoForm,
  ReferenceForm,
  ReviewDetailWrapper,
  SubmitSection,
}

const store = useStore();
const route = useRoute();
const router = useRouter();

// Popups + messaging
const helpTooltip = computed(() => TOOLTIPS.addHomeHelp);
const toastMessage = ref(null);
const showHelpSection = ref(false);
const userQuestion = ref(null);
const deleteError = ref(false);

const confirmationModal = ref(null);
const modalTitle = ref(null);
const modalText = ref(null);
const modalAction = ref(null);
const modalArgs = ref(null);
const modalButtonText = ref(null);
const isModalVisible = ref(false);

const emailSendStatus = ref(false);
const emailStatusTitle = computed(() => {
  switch (emailSendStatus.value) {
    case submitStatus.SUCCESS:
      return 'Message sent! We will reply as soon as we can.';
    case submitStatus.FAILED:
      return 'There was an issue sending your message. Please try again or reach out to us using the social media ' +
          'links at the bottom of the page.';
    default:
      return null;
  }
});
const emailAlertType = computed(() => emailSendStatus.value === submitStatus.FAILED ? 'danger' : 'success');
watch(emailSendStatus, (to) => {
  if (to) {
    // reset after a few seconds
    setTimeout(() => {
      emailSendStatus.value = false;
    }, 7000);
  }
})

const clearToast = () => {
  // remove toast message after a few seconds
  setTimeout(() => {
    toastMessage.value = null;
  }, 4000);
}
watch(toastMessage, (to) => {
  if (to) {
    clearToast();
  }
})
watch(deleteError, (to) => {
  if (to) {
    clearToast();
  }
})
const handleShowModal = (details) => {
  const {title, text, action, args, confirmButtonText} = details;
  modalTitle.value = title;
  modalText.value = text;
  modalAction.value = action;
  modalArgs.value = args;
  modalButtonText.value = confirmButtonText;
  isModalVisible.value = true;
}
const closeModal = () => {
  isModalVisible.value = false;
  modalText.value = null;
  modalTitle.value = null;
  modalAction.value = null;
  modalArgs.value = null;
  modalButtonText.value = null;
  showHelpSection.value = false;
}
const confirmClicked = () => {
  modalAction.value(modalArgs.value);
  closeModal();
}
const toEmailParams = (emailData) => {
  const params = [];
  Object.entries(emailData).forEach(([key, value]) => {
    params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  })
  return params.join("&");
}
const sendHelpRequest = () => {
  // sends request to me via email. Limit 25 per day but if we hit that I'd be surprised
  const request = new XMLHttpRequest();
  request.onreadystatechange = () => {
    if (request.readyState === 4 && request.status === 200) {
      emailSendStatus.value = submitStatus.SUCCESS;

    } else if (request.readyState === 4) {
      console.log('failed to send email: ' + request.response);
      emailSendStatus.value = submitStatus.FAILED;
    }
  };

  // fill out email
  const subject = 'Add a Home - Help Request Submitted';
  const message = `User email: ${userEmail.value}\n
      User question: ${userQuestion.value}\n
      Section sent from: ${currentPage.value?.label}\n
      Error message (if any): ${errorTooltip.value}\n
      Home form contents: ${JSON.stringify(getHomeFormSections.value)}`
  const emailData = {access_token: frontendConfig.POSTMAIL_ACCESS_TOKEN, subject, text: message};
  const params = toEmailParams(emailData);

  request.open("POST", "https://postmail.invotes.com/send", true);
  request.setRequestHeader("Content-type", "application/x-www-form-urlencoded");

  request.send(params);
}
const openHelpPopup = () => {
  // update form so it fully matches what the user sees if they end up sending a message
  shouldSaveDraft.value = true;
  showHelpSection.value = true;
  const details = {
    title: 'Ask for Help',
    text: "Having trouble completing your entry? Use the text box below to ask us a question or report an issue. " +
        "Your question, as well as the home details you've filled out so far, will be sent to the " +
        "Ancestral Homes Network team to be addressed.",
    action: sendHelpRequest,
    confirmButtonText: 'Send',
  };
  handleShowModal(details);
}

// Auth handling
const user = computed(() => store.getters.userWithSettings);
const userEmail = computed(() => {
  return user.value.contact_email || user.value.email;
})
const { isAuthenticated } = useAuth0();

const userOwnsHome = (homeOwnerId) => {
  return isAuthenticated.value && (user.value.user_id === homeOwnerId || user.value.sub === homeOwnerId);
};

// Form state
const updatedLocalStorage = ref(false);
const isMobile = computed(() => window.innerWidth <= 600);
const formIsEmpty = computed(() => store.getters.formIsEmpty);
const homeFormSections = computed(() => store.getters.getHomeFormSections);
const homeSubmitStatus = computed(() => store.getters.homeSubmitStatus);
const homeDeleteStatus = computed(() => store.getters.homeDeleteStatus);

const currentFormComponent = computed(() => formComponents[currentPage.value?.component])

watch(homeDeleteStatus, (to) => {
  if (to === submitStatus.SUCCESS) {
    // clean up photos and documents in s3
    if (homeToEdit.value.photos) {
      homeToEdit.value.photos.forEach(photoName => {
        store.dispatch('deletePhoto', photoName);
      });
    }
    if (homeToEdit.value.censuses) {
      homeToEdit.value.censuses.forEach(census => {
        if (census.pdfLinks) {
          census.pdfLinks.forEach(link => {
            store.dispatch('deletePhoto', link);
          })
        }
      });
    }
    // redirect to user profile
    router.push({name: 'profile'});
  } else if (to === submitStatus.FAILED) {
    deleteError.value = true;
  }
})

const confirmDeleteHome = (id) => {
  store.dispatch('deleteHome', id);
  closeModal();
}
const showDeletePopup = () => {
  const details = {
    title: 'Confirmation',
    text: 'Are you sure you want to delete this home? Once complete, this action cannot be undone.',
    action: confirmDeleteHome,
    args: homeToEdit.value._id,
    confirmButtonText: 'Delete',
  };
  handleShowModal(details);
}

// Draft handling
const draftInUse = ref(false);
const shouldSaveDraft = ref(false);
const storedDraft = computed(() => {
  const draftStr = localStorage.getItem(DRAFT_STORAGE_KEY);
  return JSON.parse(draftStr);
})
const isDraftOutdated = computed(() => {
  const draftMatchesInput = _.isEqual(storedDraft.value, homeFormSections.value);
  return !draftMatchesInput;
});
const draftBtnText = computed(() => {
  if (updatedLocalStorage.value) {
    // need to re-compute when this happens, but needs deep watching. So here's a useless print statement
    console.log('localstorage updated');
  }
  // Case 1: no draft saved.
  if (localStorage.getItem(DRAFT_STORAGE_KEY) === null) {
    return 'Save Draft';
  }
  // Case 2: a draft exists, and it matches the current form.
  if (!isDraftOutdated.value) {
    return 'Draft Saved';
  }
  // Case 3: a draft exists, but it doesn't match the current form.
  return 'Overwrite Existing Draft';
})
const shouldShowDraftMessage = computed(() => {
  if (updatedLocalStorage.value) {
    // need to re-compute when this happens, but needs deep watching. So here's a useless print statement
    console.log('localstorage updated');
  }
  return !draftInUse.value && !isEditPage.value && localStorage.getItem(DRAFT_STORAGE_KEY) && user.value;
})
const draftTitle = computed(() => {
  const {location, structure} = storedDraft.value;
  const prefix = structure?.data?.structName || location?.data?.country || 'Unnamed home';
  const suffix = structure?.data?.year || 'year unknown';
  return `${prefix} (${suffix})`;
})
const saveDraft = () => {
  shouldSaveDraft.value = true;
  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(homeFormSections.value));
  toastMessage.value = 'Saved Draft!'
  updatedLocalStorage.value = true;
}
const deleteDraft = () => {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
  updatedLocalStorage.value = true;
}
const restoreDraft = () => {
  store.dispatch('overwriteForm', storedDraft.value);
  draftInUse.value = true;
}

watch(updatedLocalStorage, (to) => {
  if (to) {
    // reset after a few seconds
    setTimeout(() => {
      updatedLocalStorage.value = false;
    }, 1000);
  }
})

// Navigation
const currentPageIndex = ref(0);
const shouldLeave = ref(false);
const nextButtonErrorMessage = ref(null);

const progressNodes = computed(() => {
  const selectedNodes = [];
  Object.entries(homeFormSections.value).forEach(([key, value]) => {
    if (value !== null) {
      const optionToAdd = homeFormSections.value[key];
      selectedNodes.push(optionToAdd);
    }
  })
  return selectedNodes
});
const progressNodeWidth = computed(() => {
  return {width: `calc(100% / ${progressNodes.value.length})`}
});

const currentPage = computed(() => {
  return progressNodes.value[currentPageIndex.value]
});
const isLastPage = computed(() => currentPageIndex.value === progressNodes.value.length - 1);
const hasError = computed(() => {
  return currentPage.value?.status === progressStatus.ERROR || nextButtonErrorMessage.value;
});

const errorTooltip = computed(() => {
  if (nextButtonErrorMessage.value) {
    return nextButtonErrorMessage.value;
  }
  return hasError.value ? 'This section has errors. Please fix them before continuing.' : '';
})
const canGoBack = computed(() => currentPageIndex.value > 0);
const canGoForward = computed(() => !(hasError.value || isLastPage.value));
const backButtonVisibility = computed(() => {
  return canGoBack.value ? {} : {opacity: 0};
});
const nextButtonText = computed(() => {
  const finalStep = isEditPage.value ? 'Save Changes' : 'Submit';
  return currentPage.value?.label === 'Review' ? finalStep : 'Next';
})

const getTextColour = (status) => {
  if (status === progressStatus.DONE) return { color: 'green' };
  if (status === progressStatus.ERROR) return { color: 'red' };
  return {};
};
const jumpToNode = (index) => {
  if (index < progressNodes.value.length - 1) {
    currentPageIndex.value = index;
  }
}
const disableNextButton = (message) => {
  nextButtonErrorMessage.value = message;
}
const toPreviousPage = () => {
  if (canGoBack.value) {
    currentPageIndex.value -= 1;
  }
}
const toNextPage = () => {
  if (canGoForward.value) {
    currentPageIndex.value += 1;
  }
}

watch(currentPageIndex, () => {
  // if page changes, this will need to be reset so child pages can listen for changes
  shouldSaveDraft.value = false;
});

// Editing
const homeToEdit = ref(null);
const isEditPage = ref(false);
const pageHeaderText = computed(() => isEditPage.value ? 'Edit Home' : 'Add a Home');
const existingHomeID = computed(() => homeToEdit?.value?._id);

onBeforeMount(() => {
  // check whether to display home in edit mode
  const editId = route.query.edit;
  if (editId) {
    const editHome = store.getters.getHomeToEdit;
    if (editHome && editHome._id === editId && userOwnsHome(editHome.owner_id)) {
      homeToEdit.value = editHome;
      isEditPage.value = true;
    }
  }

  if (!homeToEdit.value) {
    store.dispatch('clearForm');
  }

  // make sure submit doesn't show as 'done' yet
  store.dispatch('resetSubmitStatus');
});

onBeforeRouteLeave(async (to, from, next) => {
  await store.dispatch('clearForm');
  next();
});

</script>

<template>
  <div>
    <Alert
        v-if="shouldShowDraftMessage"
        :title="`Found a saved draft: ${draftTitle}`"
    >
      <p>
        Click 'Restore' to pick up where you left off, or click 'Delete' to remove the draft altogether.
        You can also ignore this banner and come back to your draft later.
      </p>
      <div class="alert-buttons">
        <button class="btn btn-primary" @click="restoreDraft">Restore</button>
        <button class="btn btn-danger" @click="deleteDraft">Delete</button>
      </div>
    </Alert>
    <Alert v-if="toastMessage" :title="toastMessage" floating/>
    <Alert
        v-if="deleteError"
        :title="'Something went wrong while deleting. Please try again.'"
        :alert-type="'danger'"
        floating
    />
    <Alert v-if="emailSendStatus" :title="emailStatusTitle" :alert-type="emailAlertType" floating/>

    <div class="page-bg">
      <div v-if="isAuthenticated">
        <div class="page-header">
          <div class="title-section">
            <h1 class="title-font">{{ pageHeaderText }}</h1>
            <p>
              Follow the steps outlined below to fill out an entry for the Ancestral Homes database.
              <br>
              Once you've filled out all of the details you want for a particular section, click the 'next' button at
              the
              bottom of the page to move on to the next one.
            </p>
          </div>
          <button v-if="isMobile" class="btn btn-secondary" @click="openHelpPopup">?</button>
          <button v-else class="btn btn-secondary" v-tooltip="helpTooltip" @click="openHelpPopup">Need Help?</button>
        </div>
        <div :class="['page-body', {'light-teal-bg': !isMobile}]">
          <table v-if="isMobile" class="prog-table-mobile">
            <tr
                v-for="(node, index) in progressNodes"
                :key="index"
            >
              <td class="left-cell" @click="jumpToNode(index)">{{ node.label }}</td>
              <td class="right-cell" :style="getTextColour(node.status)">{{ node.status || '-' }}</td>
            </tr>
          </table>
          <hr v-if="isMobile"/>
          <ProgressBar v-else
            :progress-nodes="progressNodes"
            :current-page-index="currentPageIndex"
            @nodeClicked="jumpToNode"
          />
          <div class="form-contents">
            <component
              :is="currentFormComponent"
              :edit-id="existingHomeID"
              :trigger-save-draft="shouldSaveDraft"
              @disableNext="disableNextButton"
              @showModal="handleShowModal"
            />
          </div>
          <hr v-if="isMobile"/>
          <div class="bottom-buttons" v-if="!isLastPage">
            <button :style="backButtonVisibility" class="btn btn-primary" @click="toPreviousPage">
              Back
            </button>
            <button v-if="isEditPage" class="btn btn-danger" @click="showDeletePopup">Delete Home</button>
            <button class="btn btn-secondary" @click="saveDraft">{{ draftBtnText }}</button>
            <button v-if="hasError" class="btn btn-danger" @click="openHelpPopup">Ask for Help</button>
            <button
                :class="['btn','btn-primary', {'btn-disabled': !canGoForward}]"
                v-tooltip="errorTooltip"
                @click="toNextPage"
            >
              {{ nextButtonText }}
            </button>
            <p class="warning-text" v-if="isMobile && errorTooltip">{{ errorTooltip }}</p>
          </div>
        </div>
      </div>
      <div v-else>
        <AddHomeLoginPrompt></AddHomeLoginPrompt>
      </div>
    </div>

    <Modal
      ref="confirmationModal"
      class="add-home-modal"
      v-if="isModalVisible"
      :primary-action="modalButtonText"
      @close="closeModal"
      @confirm="confirmClicked"
    >
      <template #header>
        <h3 class="modal-title">{{ modalTitle }}</h3>
      </template>
      <template #body>
        <p class="modal-body">{{ modalText }}</p>
        <div v-if="showHelpSection" class="help-message">
          <div v-if="hasError && errorTooltip" class="error-printout">
            <h5>It looks like you're unable to move forward for the following reason:</h5>
            <p class="modal-body">{{ errorTooltip }}</p>
          </div>
          <textarea v-model="userQuestion" class="help-textbox" id="helpTextbox"/>
          <p>A response to your question will be sent to {{ userEmail }} within 24 hours.</p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped>
/* draft alert */
.alert-buttons {
  margin-bottom: -0.5rem;
}

.alert-buttons .btn {
  margin: 1rem 1rem 0 0;
}

/* logged in view */
.page-header {
  display: flex;
}

.page-header button {
  margin-left: auto;
  height: 90%;
}

.title-section p {
  font-size: 14px;
}

.btn {
  font-size: large;
  padding: 0.5rem 1rem;
}

.page-body {
  padding: 1rem;
  border-radius: 4px;
}

.form-contents {
  background: white;
  min-height: 60vh;
  border: 1px lightgrey solid;
  padding: 1rem;
}

.bottom-buttons {
  padding-top: 1rem;
  display: flex;
  justify-content: space-between;
}

/* help popup */
.modal-title {
  margin: 0;
}

.modal-body {
  font-size: medium;
}

.help-message {
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
}

.error-printout {
  background-color: #f5c6cb;
  border: 1px solid #a92222;
  padding: 0.5rem;
  margin-bottom: 1rem;
}

.help-textbox {
  min-height: 20vh;
}

@media screen and (max-width: 600px) {
  /*logged in view (mobile)*/
  .page-body {
    padding: 0;
  }

  .form-contents {
    padding: 0;
    border: none;
  }

  .page-header button {
    border-radius: 50%;
    width: 45px;
    height: 45px;
  }

  .prog-table-mobile {
    width: 100%;
    margin: 1rem 0;
  }

  .left-cell {
    cursor: pointer;
  }

  .right-cell {
    text-align: right;
  }

  .bottom-buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .bottom-buttons .btn {
    margin-bottom: 1rem;
    width: 100%;
  }
}
</style>